import { useState, useEffect } from 'react';

/**
 * Custom hook for managing Iubenda consent management platform
 * Works with the Iubenda configuration in public/index.html
 */
const useIubenda = () => {
  const [isIubendaLoaded, setIsIubendaLoaded] = useState(false);

  // Check if _iub is available in the window
  const checkIubendaAvailability = () => {
    return typeof window !== 'undefined' && 
           typeof window._iub !== 'undefined' && 
           typeof window._iub.cs !== 'undefined';
  };

  useEffect(() => {
    // Initialize Iubenda check
    const checkIubendaLoaded = () => {
      if (checkIubendaAvailability()) {
        setIsIubendaLoaded(true);
      }
    };

    // Check immediately if Iubenda is already loaded
    checkIubendaLoaded();

    // Set up interval to check for Iubenda loading
    const intervalId = setInterval(() => {
      if (!isIubendaLoaded) {
        checkIubendaLoaded();
      } else {
        clearInterval(intervalId);
      }
    }, 500);

    // Clean up interval on component unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [isIubendaLoaded]);

  /**
   * Show the Iubenda consent banner
   */
  const showConsentBanner = () => {
    if (checkIubendaAvailability()) {
      window._iub.cs.showBanner();
    } else {
      console.warn('Iubenda is not loaded yet');
    }
  };

  /**
   * Hide the Iubenda consent banner
   */
  const hideConsentBanner = () => {
    if (checkIubendaAvailability()) {
      window._iub.cs.hideBanner();
    } else {
      console.warn('Iubenda is not loaded yet');
    }
  };

  /**
   * Check if user has given consent to specific purpose
   * @param {string} purposeId - The purpose ID to check consent for
   * @returns {boolean} - true if consent is given, false otherwise
   */
  const hasConsent = (purposeId) => {
    if (checkIubendaAvailability()) {
      try {
        return window._iub.cs.api.purposes.consented(purposeId);
      } catch (e) {
        console.error('Error checking consent status:', e);
        return false;
      }
    }
    return false;
  };

  /**
   * Check if the user has interacted with the consent banner
   * @returns {boolean} - true if user has interacted with the banner
   */
  const hasInteractedWithBanner = () => {
    if (checkIubendaAvailability()) {
      try {
        return window._iub.cs.api.consentGiven();
      } catch (e) {
        console.error('Error checking banner interaction:', e);
        return false;
      }
    }
    return false;
  };

  /**
   * Open the preferences panel
   */
  const openPreferences = () => {
    if (checkIubendaAvailability()) {
      window._iub.cs.api.openPreferences();
    } else {
      console.warn('Iubenda is not loaded yet');
    }
  };

  return {
    isIubendaLoaded,
    showConsentBanner,
    hideConsentBanner,
    hasConsent,
    hasInteractedWithBanner,
    openPreferences
  };
};

export default useIubenda;

